// import components
import { Checkbox } from '@mui/material';

// import constants
import { BACKLOG_FILTER_SUMMARY_MAX_CHARS } from '../../Backlog.consts';

// import utils
import { entityFiltersForView } from '../../Backlog.utils';

// import styles
import { BacklogFilterTagSummary } from '../../Backlog.styles';

// import types
import type { HTMLAttributes, Key } from 'react';
import type { PlanningEntityType } from 'generated/index';
import type {
  BacklogEmployeeViewFilters,
  BacklogEntityFilter,
  FilterOption
} from '../../Backlog.types';
import type { BacklogPlanUnitOption } from './components/BacklogPlanUnitFilter/BacklogPlanUnitFilter.types';
import type { AutocompleteRenderOptionState } from '@mui/material';

/**
 * What a filter field shows for its selection. One pick is named, so the planner can see *what* is
 * filtered; past that the names are dropped for a plain count — a planner picking several activities or
 * several employees would otherwise be reading a wall of chips in a field one line tall.
 *
 * A single name longer than {@link BACKLOG_FILTER_SUMMARY_MAX_CHARS} is reported as a count too: it
 * cannot be read in the space available, and half a name is worth less than knowing one thing is picked.
 *
 * Always a single element, never MUI's chip per option: the field must stay one line however much is
 * picked, and the summary ellipsises rather than wrapping (see {@link BacklogFilterTagSummary}).
 */
export const renderFilterTags =
  <Option,>(
    getOptionLabel: (option: Option) => string,
    selectedLabel: string
  ) =>
  (selected: Option[]) => {
    const onlyPick = selected.length === 1 ? getOptionLabel(selected[0]) : null;
    const fits =
      onlyPick !== null && onlyPick.length <= BACKLOG_FILTER_SUMMARY_MAX_CHARS;

    return (
      <BacklogFilterTagSummary>
        {fits ? onlyPick : `${selected.length} ${selectedLabel}`}
      </BacklogFilterTagSummary>
    );
  };

/**
 * A filter option with a checkbox, and a list that stays open while you tick.
 *
 * Every one of these fields takes several picks, and MUI closes its list after each one by default — so a
 * planner had to reopen the field per activity. The checkbox is what says the field is a multiple choice in
 * the first place; keeping the list open is the field's own `disableCloseOnSelect`.
 *
 * Rendered here rather than through the shared MuiAutocompleteOption: that component keeps the props it is
 * handed for itself, so the `li` loses the role, id and aria-selected MUI put there — the list would
 * announce as a listbox with no options in it. Its search-term highlighting is what we give up for that.
 */
export const renderFilterOption =
  <Option,>(getOptionLabel: (option: Option) => string) =>
  (
    props: Omit<HTMLAttributes<HTMLLIElement>, 'children'>,
    option: Option,
    { selected }: AutocompleteRenderOptionState
  ) => {
    // MUI puts the list key in the same object as the DOM props, and React refuses a spread key.
    const { key, ...optionProps } = props as HTMLAttributes<HTMLLIElement> & {
      key?: Key;
    };

    return (
      <li key={key} {...optionProps}>
        <Checkbox size="small" checked={selected} />
        {getOptionLabel(option)}
      </li>
    );
  };

/**
 * The cost centre ids the picked plan units stand for — what scopes the employee view, both in the lists
 * beside it and on the backlog request itself. Empty when nothing is picked.
 *
 * A plan unit's cost-centre id is its `uniqId`, not its `id`; a plan group's `uniqId` is a string like
 * "5-group", which is no cost centre at all, so a group contributes the cost centres it holds. That is the
 * distinction the roster plan makes too, and reading `id` instead leaves every list empty.
 */
export const costCenterIdsOf = (planUnits: BacklogPlanUnitOption[]): number[] =>
  planUnits
    .flatMap((planUnit) =>
      'costCenters' in planUnit && Array.isArray(planUnit.costCenters)
        ? planUnit.costCenters.map(({ id }) => id)
        : [planUnit.uniqId]
    )
    .filter((id): id is number => typeof id === 'number');

/**
 * The same ids in the comma-separated form both the employees and the functions call expect — the shape the
 * roster plan builds for its own employee list. Empty when nothing is picked, which is what those fields
 * read as "no list to offer yet".
 */
export const costCentersOf = (planUnits: BacklogPlanUnitOption[]): string =>
  costCenterIdsOf(planUnits).join(',');

/**
 * How many filters the toolbar's badge reports: one per field that has something in it, not one per value —
 * two activities and three employees is two filters, not five.
 *
 * Counted over the fields the view on screen actually offers. The popover shows one entity's filters at a
 * time, and a remembered pick from another view narrows nothing here, so counting it would send a planner
 * hunting through a popover for a filter it does not show. The activity is counted in every view: it is the
 * one filter that means the same thing wherever you are.
 */
export const countActiveFilters = ({
  rowEntityType,
  selectedActivityOptions,
  entityFilters,
  employeeViewFilters
}: {
  rowEntityType: PlanningEntityType;
  selectedActivityOptions: FilterOption[];
  entityFilters: BacklogEntityFilter[];
  employeeViewFilters: BacklogEmployeeViewFilters;
}): number => {
  const setFields =
    rowEntityType === 'EMPLOYEE'
      ? // The employee view filters on its own three fields; its entity picks are not among them, since the
        // employee list carries numeric ids rather than the planning entity uuids a filter would need.
        [
          employeeViewFilters.planUnitIds,
          employeeViewFilters.employees,
          employeeViewFilters.functions
        ]
      : [entityFiltersForView(entityFilters, rowEntityType)];

  return (
    (selectedActivityOptions.length > 0 ? 1 : 0) +
    setFields.filter(({ length }) => length > 0).length
  );
};
